import { KERB_LANE } from '@accra/shared';
import { Group } from 'three';
import type { RunState, Walker } from '../../game/RunState';
import { laneX } from '../../systems/lanes';
import { STEP_OFF, WALK_AWAY, WALK_TO_DOOR } from '../../systems/passengers/walking';
import { animateWalk, createPerson, type Person } from '../people/personModel';
import { crowdSpot } from './crowd';
import { LAYOUT } from './roadLayout';

const POOL = 12;

export interface DoorInfo {
  /** Distance of the sliding door from the bus centre, towards the rear (+z). */
  offsetZ: number;
  halfWidth: number;
}

interface Point {
  x: number;
  z: number;
}

function lerp(a: Point, b: Point, t: number): Point {
  return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
}

/** Animates people walking from the crowd to the door, and away from it after getting down. */
export class Walkers {
  readonly group = new Group();
  private readonly pool: Person[] = [];
  private readonly assigned = new Map<number, Person>();

  constructor() {
    for (let i = 0; i < POOL; i += 1) {
      const person = createPerson(500 + i * 17);
      person.root.visible = false;
      this.pool.push(person);
      this.group.add(person.root);
    }
  }

  update(state: RunState, door: DoorInfo): void {
    const live = new Set(state.walkers.map((w) => w.id));
    for (const [id, person] of this.assigned) {
      if (live.has(id)) continue;
      person.root.visible = false;
      this.assigned.delete(id);
    }
    for (const walker of state.walkers) {
      const person = this.personFor(walker.id);
      if (!person) continue;
      person.root.visible = true;
      if (walker.kind === 'board') this.placeBoarder(person, walker, state, door);
      else this.placeAlighter(person, walker, door);
    }
  }

  private personFor(id: number): Person | undefined {
    const existing = this.assigned.get(id);
    if (existing) return existing;
    const free = this.pool.find((p) => ![...this.assigned.values()].includes(p));
    if (free) this.assigned.set(id, free);
    return free;
  }

  private doorAt(busS: number, door: DoorInfo): Point {
    return { x: laneX(KERB_LANE) + door.halfWidth + 0.35, z: -busS + door.offsetZ };
  }

  private placeBoarder(person: Person, walker: Walker, state: RunState, door: DoorInfo): void {
    const from = crowdSpot(state.stop.seq, walker.fromSlot);
    const to = this.doorAt(state.bus.s, door);
    const t = Math.min(1, walker.t / WALK_TO_DOOR);
    const p = lerp(from, to, t);
    person.root.position.set(p.x, 0, p.z);
    person.root.rotation.y = Math.atan2(to.x - from.x, to.z - from.z);
    animateWalk(person, walker.t * 9, 1);
    person.root.position.y += t > 0.8 ? (t - 0.8) * 1.5 : 0.2 * (1 - t);
  }

  private placeAlighter(person: Person, walker: Walker, door: DoorInfo): void {
    const start = this.doorAt(walker.s, door);
    const kerb = { x: LAYOUT.sidewalkX - 0.4, z: start.z + 1.2 };
    const stepTime = STEP_OFF + 0.5;
    let p: Point;
    let heading: number;
    if (walker.t < stepTime) {
      p = lerp(start, kerb, walker.t / stepTime);
      heading = Math.atan2(kerb.x - start.x, kerb.z - start.z);
    } else {
      // Then off along the pavement, back the way the trotro came.
      p = { x: kerb.x + 0.6, z: kerb.z + (walker.t - stepTime) * 1.3 };
      heading = 0;
    }
    person.root.position.set(p.x, 0.2, p.z);
    person.root.rotation.y = heading;
    animateWalk(person, walker.t * 9, 1);
    person.root.visible = walker.t < WALK_AWAY - 0.1;
  }
}
